import { getSupabase } from './supabaseClient';

export const SPECIALIST_IMAGE_BUCKET = 'specialist-images';
export const SPECIALIST_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
export const SPECIALIST_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);
const DEFAULT_SPECIALIST_IMAGE_POSITION = 'center center';
const SPECIALIST_SELECT_FIELDS =
  'id, name, role, specialization, experience, languages, bio, image_path, image_position, sort_order, is_active, updated_by, created_at, updated_at';
const SPECIALIST_PLACEHOLDER_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 420">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#f6eee3" />
        <stop offset="100%" stop-color="#e7d4ba" />
      </linearGradient>
    </defs>
    <rect width="640" height="420" fill="url(#bg)" />
    <circle cx="320" cy="160" r="74" fill="#d3b089" opacity="0.95" />
    <path d="M196 340c26-68 82-102 124-102s98 34 124 102" fill="#c49a6f" opacity="0.95" />
    <text x="320" y="390" text-anchor="middle" fill="#6b5a4e" font-family="Arial, sans-serif" font-size="28">
      Specialist image
    </text>
  </svg>`
)}`;

export const DEFAULT_SPECIALISTS = [
  {
    id: 'madlen',
    name: 'Madlen',
    role: 'Kahuna Master Practitioner',
    specialization:
      'Life Therapy, Spiritual Aromatherapy, Raja Yoga, Attracting Abundance, Crystal Healing, Classical Reiki, Spiritual Counseling, Karuna Ki Reiki, Energy Balance Practitioner',
    experience: '21 years',
    languages: 'English, Arabic, Bulgarian',
    bio:
      'Masters in Spiritual Healing, Master Life Coach, American Union of NLP (USA) holistic health practitioner, and registered healer with international natural healing credentials.',
    imageSrc:
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
    imagePosition: DEFAULT_SPECIALIST_IMAGE_POSITION,
    sortOrder: 10
  },
  {
    id: 'nadea',
    name: 'Nadea',
    role: 'Reiki Master',
    specialization: 'Reiki, Huna Healing, Breathing techniques, Meditation',
    experience: '13 years',
    languages: 'English, Russian',
    bio:
      'Internationally certified Reiki Usui Shiki Ryoho Master, Kundalini, Quantum Alchemy and Hawaiian Huna wellness practitioner.',
    imageSrc:
      'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=1200&q=80',
    imagePosition: DEFAULT_SPECIALIST_IMAGE_POSITION,
    sortOrder: 20
  },
  {
    id: 'anastasia',
    name: 'Anastasia',
    role: 'Astrologer, Coach, Energy Practitioner',
    specialization:
      'Relationship diagnostics, healing, post-traumatic closure, karmic issues diagnostics, and correction',
    experience: '14 years',
    languages: 'English, Russian',
    bio:
      'Internationally certified in Kundalini Reiki, Runes, Crystal Healing, Feng Shui, Astrology, Tarot, Life Coaching, Goal to Success Coaching, and couple relationship guidance.',
    imageSrc:
      'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1200&q=80',
    imagePosition: DEFAULT_SPECIALIST_IMAGE_POSITION,
    sortOrder: 30
  }
];

const DEFAULT_SPECIALIST_BY_ID = new Map(DEFAULT_SPECIALISTS.map((item) => [item.id, item]));

const sanitizeText = (value, fallback = '') =>
  typeof value === 'string' ? value.trim() : fallback;

const normalizeSortOrder = (value, fallback = 999) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const buildPublicImageUrl = (imagePath, version) => {
  if (!imagePath) {
    return '';
  }
  const {
    data: { publicUrl }
  } = getSupabase().storage.from(SPECIALIST_IMAGE_BUCKET).getPublicUrl(imagePath);

  if (!version) {
    return publicUrl;
  }

  const separator = publicUrl.includes('?') ? '&' : '?';
  return `${publicUrl}${separator}v=${encodeURIComponent(version)}`;
};

export const slugifySpecialistId = (value = '') => {
  const slug = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  return slug || `specialist-${Date.now()}`;
};

export const normalizeSpecialistRecord = (row = {}) => {
  const fallback = DEFAULT_SPECIALIST_BY_ID.get(row.id) || {};
  const imagePath = sanitizeText(row.image_path, '');
  const updatedAt = row.updated_at || null;

  return {
    id: sanitizeText(row.id, fallback.id || ''),
    name: sanitizeText(row.name, fallback.name || 'Untitled specialist'),
    role: sanitizeText(row.role, fallback.role || ''),
    specialization: sanitizeText(row.specialization, fallback.specialization || ''),
    experience: sanitizeText(row.experience, fallback.experience || ''),
    languages: sanitizeText(row.languages, fallback.languages || ''),
    bio: sanitizeText(row.bio, fallback.bio || ''),
    imagePath: imagePath || null,
    imageSrc: imagePath
      ? buildPublicImageUrl(imagePath, updatedAt)
      : fallback.imageSrc || SPECIALIST_PLACEHOLDER_IMAGE,
    imagePosition:
      sanitizeText(row.image_position, fallback.imagePosition || DEFAULT_SPECIALIST_IMAGE_POSITION) ||
      DEFAULT_SPECIALIST_IMAGE_POSITION,
    sortOrder: normalizeSortOrder(row.sort_order, fallback.sortOrder ?? 999),
    isActive: row.is_active !== false,
    createdAt: row.created_at || null,
    updatedAt,
    updatedBy: sanitizeText(row.updated_by, '')
  };
};

export const mergeSpecialistsWithRows = (rows = []) => {
  return (rows || [])
    .map((row) => normalizeSpecialistRecord(row))
    .filter((item) => item.isActive)
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
};

export const fetchSpecialistRows = async ({ includeInactive = false } = {}) => {
  let query = getSupabase()
    .from('specialists')
    .select(SPECIALIST_SELECT_FIELDS)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  return query;
};

export const buildSpecialistImagePath = (specialistId, fileName = 'upload.jpg') => {
  const extensionMatch = /\.([a-z0-9]+)$/i.exec(fileName);
  const rawExtension = extensionMatch ? extensionMatch[1].toLowerCase() : 'jpg';
  const extension = ALLOWED_IMAGE_EXTENSIONS.has(rawExtension) ? rawExtension : 'jpg';
  return `${specialistId}/profile.${extension}`;
};

export const createSpecialistDraft = (specialist = {}) => ({
  id: sanitizeText(specialist.id, ''),
  name: sanitizeText(specialist.name, ''),
  role: sanitizeText(specialist.role, ''),
  specialization: sanitizeText(specialist.specialization, ''),
  experience: sanitizeText(specialist.experience, ''),
  languages: sanitizeText(specialist.languages, ''),
  bio: sanitizeText(specialist.bio, ''),
  imagePosition:
    sanitizeText(specialist.imagePosition, DEFAULT_SPECIALIST_IMAGE_POSITION) ||
    DEFAULT_SPECIALIST_IMAGE_POSITION,
  sortOrder: String(normalizeSortOrder(specialist.sortOrder, DEFAULT_SPECIALISTS.length * 10 + 10))
});
